import Vue from 'vue';
import VueRouter from 'vue-router';
import { Modal } from 'ant-design-vue';

Vue.use(VueRouter);

/**
 * Vue Router 3.x 兼容处理：
 * push/replace 被导航守卫重定向时会返回 rejected Promise，
 * 未捕获会触发全局错误遮罩（"Redirected when going from ... via a navigation guard"），
 * 此处统一静默吞掉此类预期内的重定向错误
 */
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;

function silentCatch(result) {
  if (result && typeof result.catch === 'function') {
    return result.catch(err => err);
  }
  return result;
}

VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return silentCatch(originalPush.call(this, location));
};

VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }
  return silentCatch(originalReplace.call(this, location));
};

// 路由懒加载 - 避免首屏加载过大
const Login = () => import('@/views/login/index.vue');
const Register = () => import('@/views/register/index.vue');
const Layout = () => import('@/views/dashboard/Layout.vue');
const DashboardHome = () => import('@/views/dashboard/Home.vue');
const MemoryWall = () => import('@/views/memory/wall/Wall.vue');
const MemoryMap = () => import('@/views/memory/map/MemoryMap.vue');
const PersonList = () => import('@/views/person/list/List.vue');
const TimeCapsule = () => import('@/views/memory/capsule/TimeCapsule.vue');

const routes = [
  {
    path: '/',
    redirect: '/login' // 根路径重定向到登录页
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { 
      title: '登录 - 少年回忆录',
      requiresAuth: false // 标记：无需登录即可访问
    }
  },
  {
    // 隐藏注册页：不开放任何入口按钮，仅限管理员通过浏览器手动输入 URL 访问
    // 必须使用 admin 账号登录后才能打开（后端接口同时做权威校验）
    path: '/register',
    name: 'Register',
    component: Register,
    meta: {
      title: '注册 - 少年回忆录',
      requiresAuth: true,
      adminOnly: true,
      hidden: true
    }
  },
  {
    path: '/dashboard',
    component: Layout,
    meta: { 
      title: '控制台 - 少年回忆录',
      requiresAuth: true // 标记：需要登录才能访问
    },
    children: [
      {
        path: '',
        name: 'DashboardHome',
        component: DashboardHome,
        meta: { title: '控制台 | 少年回忆录' }
      },
      {
        path: 'wall',
        name: 'MemoryWall',
        component: MemoryWall,
        meta: { title: '回忆墙 | 少年回忆录' }
      },
      {
        path: 'map',
        name: 'MemoryMap',
        component: MemoryMap,
        meta: { title: '时光地图 | 少年回忆录' }
      },
      {
        path: 'people',
        name: 'PersonList',
        component: PersonList,
        meta: { title: '人物谱 | 少年回忆录' }
      },
      {
        path: 'capsule',
        name: 'TimeCapsule',
        component: TimeCapsule,
        meta: { title: '时光胶囊 | 少年回忆录' }
      }
    ]
  },
  {
    // 404 兜底路由
    path: '*',
    redirect: '/login'
  }
];

const router = new VueRouter({
  mode: 'history', // 使用 history 模式，URL 更美观
  base: process.env.BASE_URL,
  routes
});

/**
 * 解析 JWT payload 并判断 token 是否过期
 * 后端签发有效期为 1 小时，过期后必须重新登录
 * @param {string} token - JWT 字符串
 * @returns {boolean} true = 已过期或无效
 */
function isTokenExpired(token) {
  try {
    const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(decodeURIComponent(atob(payloadBase64)
      .split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
    if (!payload.exp) return false;
    return payload.exp * 1000 <= Date.now();
  } catch (e) {
    return true; // 解析失败视为无效
  }
}

/**
 * 🔐 全局前置守卫 - 核心鉴权逻辑
 */
router.beforeEach((to, from, next) => {
  // 1. 动态设置页面标题
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  // 2. 检查是否需要鉴权（遍历匹配的路由，包含父路由）
  const token = localStorage.getItem('token');
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  // token 存在但已过期 → 清理本地登录信息，后续按未登录处理
  if (token && isTokenExpired(token)) {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  }
  const validToken = localStorage.getItem('token');

  if (requiresAuth) {
    // 目标页面需要登录
    if (validToken) {
      // 管理员专属页面：额外校验当前登录者是否为 admin（后端接口仍会做权威校验）
      const adminOnly = to.matched.some(record => record.meta.adminOnly);
      if (adminOnly) {
        let userInfo = null;
        try {
          userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
        } catch (e) {
          userInfo = null;
        }
        if (!userInfo || userInfo.username !== 'admin') {
          // 直接重定向回控制台（不能用 next(false)+弹窗后 replace，
          // 否则中断导航后再跳转会触发 NavigationRedirected 错误）
          next('/dashboard');
          Modal.warning({
            title: '无权访问',
            content: '该页面仅限管理员访问，请使用管理员账号登录。',
            okText: '知道了',
            centered: true
          });
          return;
        }
      }
      next(); // ✅ 有有效 token，放行
    } else {
      // ❌ 无 token，弹窗提示后再跳转登录页
      // 先阻止路由切换，停留在当前页面
      next(false);
      
      Modal.confirm({
        title: '登录已过期',
        content: '当前未登录或登录信息已过期，请重新登录后继续操作。',
        okText: '去登录',
        cancelText: '关闭',
        centered: true,
        onOk() {
          router.push({ path: '/login', query: { redirect: to.fullPath } });
        },
        onCancel() {
          router.push({ path: '/login', query: { redirect: to.fullPath } });
        }
      });
    }
  } else {
    // 目标页面不需要登录（如登录页本身）
    if (to.path === '/login' && validToken) {
      // 已登录用户访问登录页 → 直接跳转到控制台
      next('/dashboard');
    } else {
      next(); // ✅ 正常放行
    }
  }
});

export default router;